import ProductShowcase from '../components/ProductShowcase';
import React, { useEffect, useState, useRef } from 'react';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import styled from 'styled-components';
import { FiStar, FiHeart, FiAward, FiTruck, FiChevronRight } from 'react-icons/fi';
import ProductCard from '../components/ProductCard';
import { getProducts } from '../api';

const HomeContainer = styled.div`
  overflow-x: hidden;
`;

const HeroSection = styled.section`
  position: relative;
  height: 100vh;
  width: 100%;
  background: var(--dark-bg);
  overflow: hidden;
`;

const ParallaxLayer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 120%;
  will-change: transform;
`;

const BackgroundImage = styled(ParallaxLayer)`
  background-image: url('/assets/produ.png');
  background-size: cover;
  background-position: center;
  opacity: 0.5;
  z-index: 1;
`;

const GradientOverlay = styled(ParallaxLayer)`
  background: radial-gradient(circle at 30% 50%, rgba(211,149,255,0.3) 0%, transparent 60%),
              radial-gradient(circle at 70% 50%, rgba(255,127,217,0.3) 0%, transparent 60%);
  z-index: 2;
`;

const PatternOverlay = styled(ParallaxLayer)`
  background-image: url('/assets/pattern.svg');
  background-size: 100px 100px;
  opacity: 0.05;
  z-index: 3;
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 10;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  padding: 0 10%;
  color: white;
  max-width: 1400px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 0 5%;
    align-items: center;
    text-align: center;
  }
`;

const HeroBadge = styled(motion.span)`
  display: inline-block;
  padding: 0.5rem 1rem;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 30px;
  font-size: 0.9rem;
  letter-spacing: 1px;
  margin-bottom: 2rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: rgba(255, 255, 255, 0.9);
`;

const HeroTitle = styled(motion.h1)`
  font-size: 5.5rem;
  font-weight: 800;
  line-height: 1.1;
  margin-bottom: 1.5rem;
  max-width: 800px;
  color: white;

  span {
    background: linear-gradient(135deg, #d395ff, #ff7fd9);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    display: inline-block;
  }

  @media (max-width: 768px) {
    font-size: 3rem;
  }
`;

const HeroDescription = styled(motion.p)`
  font-size: 1.2rem;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.7);
  max-width: 600px;
  margin-bottom: 2.5rem;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const HeroStats = styled(motion.div)`
  display: flex;
  gap: 3rem;
  margin-top: 2rem;

  @media (max-width: 768px) {
    justify-content: center;
    flex-wrap: wrap;
    gap: 2rem;
  }
`;

const StatItem = styled.div`
  text-align: left;

  @media (max-width: 768px) {
    text-align: center;
  }
`;

const StatNumber = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: white;
  margin-bottom: 0.3rem;
`;

const StatLabel = styled.div`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const CTAButton = styled(motion.button)`
  padding: 1.2rem 3rem;
  font-size: 1.1rem;
  font-weight: 600;
  color: white;
  background: linear-gradient(135deg, #d395ff, #ff7fd9);
  border: none;
  border-radius: 8px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-2px);
  }

  &::after {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: linear-gradient(
      transparent,
      rgba(255, 255, 255, 0.1),
      transparent
    );
    transform: rotate(45deg);
    animation: shimmer 3s infinite;
  }

  @keyframes shimmer {
    0% { transform: translateX(-100%) rotate(45deg); }
    100% { transform: translateX(100%) rotate(45deg); }
  }
`;

const ScrollIndicator = styled(motion.div)`
  position: absolute;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  color: white;
  cursor: pointer;
  z-index: 10;
`;

const ScrollLine = styled(motion.div)`
  width: 2px;
  height: 60px;
  background: linear-gradient(180deg, white, transparent);
`;

const ScrollText = styled.span`
  font-size: 0.8rem;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.5);
`;

const FeaturedSection = styled.section`
  position: relative;
  padding: 6rem 10%;
  background: white;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 3rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--text-dark);
  line-height: 1.2;

  span {
    display: block;
    font-size: 1rem;
    color: var(--primary-color);
    margin-bottom: 0.5rem;
    text-transform: uppercase;
    letter-spacing: 2px;
  }

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const ViewAllLink = styled(motion.a)`
  color: var(--text-dark);
  text-decoration: none;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border-bottom: 2px solid transparent;
  transition: border-color 0.3s ease;

  &:hover {
    border-bottom-color: var(--primary-color);
  }
`;

const ProductsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FeaturesSection = styled.section`
  padding: 4rem 10%;
  background: linear-gradient(135deg, #f8f8f8, #ffffff);
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;

  @media (max-width: 968px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 568px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled(motion.div)`
  text-align: center;
  padding: 2rem;
  background: white;
  border-radius: 16px;
  box-shadow: var(--shadow-sm);
  border: 1px solid var(--border-light);

  svg {
    font-size: 2.5rem;
    color: var(--primary-color);
    margin-bottom: 1rem;
  }

  h3 {
    font-size: 1.2rem;
    color: var(--text-dark);
    margin-bottom: 0.5rem;
  }

  p {
    color: var(--text-gray);
    font-size: 0.95rem;
    line-height: 1.5;
  }
`;

const ParallaxBanner = styled.section`
  position: relative;
  height: 50vh;
  overflow: hidden;
`;

const BannerImage = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 120%;
  background-image: url('/assets/banner-bg.jpg');
  background-size: cover;
  background-position: center;
  will-change: transform;
`;

const BannerContent = styled.div`
  position: relative;
  z-index: 2;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  color: white;
  background: rgba(0, 0, 0, 0.4);
  padding: 0 20px;
`;

const BannerTitle = styled.h3`
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const BannerSubtitle = styled.p`
  font-size: 1.2rem;
  max-width: 600px;
  margin-bottom: 2rem;
  opacity: 0.9;
`;

const BannerButton = styled(motion.button)`
  padding: 1rem 3rem;
  font-size: 1rem;
  background: transparent;
  color: white;
  border: 2px solid white;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: white;
    color: var(--dark-bg);
  }
`;

const TestimonialsSection = styled.section`
  padding: 6rem 10%;
  background: white;
`;

const TestimonialsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin-top: 3rem;

  @media (max-width: 968px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 568px) {
    grid-template-columns: 1fr;
  }
`;

const TestimonialCard = styled(motion.div)`
  padding: 2rem;
  background: #f8f8f8;
  border-radius: 16px;
  position: relative;

  &::before {
    content: '"';
    position: absolute;
    top: 1rem;
    left: 1rem;
    font-size: 4rem;
    color: var(--primary-color);
    opacity: 0.3;
    font-family: serif;
  }

  p {
    color: var(--text-gray);
    line-height: 1.8;
    margin-bottom: 1.5rem;
    font-style: italic;
    position: relative;
    z-index: 1;
  }

  .author {
    display: flex;
    align-items: center;
    gap: 1rem;

    img {
      width: 50px;
      height: 50px;
      border-radius: 50%;
      object-fit: cover;
    }

    div {
      h4 {
        font-size: 1rem;
        color: var(--text-dark);
        margin-bottom: 0.2rem;
      }

      span {
        font-size: 0.8rem;
        color: var(--text-light-gray);
      }
    }
  }
`;

const InstagramSection = styled.section`
  padding: 4rem 10%;
  background: linear-gradient(135deg, #f8f8f8, #ffffff);
  text-align: center;
`;

const InstagramGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  margin: 3rem 0;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const InstagramPost = styled(motion.div)`
  aspect-ratio: 1;
  background: #f0f0f0;
  border-radius: 12px;
  overflow: hidden;
  position: relative;
  cursor: pointer;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.6s ease;
  }

  &:hover img {
    transform: scale(1.1);
  }

  &::after {
    content: '📸';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 2rem;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover::after {
    opacity: 1;
  }
`;

const NewsletterSection = styled.section`
  padding: 4rem 10%;
  background: var(--dark-bg);
  color: white;
  text-align: center;
`;

const NewsletterForm = styled.form`
  max-width: 500px;
  margin: 2rem auto 0;
  display: flex;
  gap: 1rem;

  @media (max-width: 568px) {
    flex-direction: column;
  }

  input {
    flex: 1;
    padding: 1rem;
    border: none;
    border-radius: 8px;
    font-size: 1rem;

    &:focus {
      outline: 2px solid var(--primary-color);
    }
  }

  button {
    padding: 1rem 2rem;
    background: linear-gradient(135deg, #d395ff, #ff7fd9);
    color: white;
    border: none;
    border-radius: 8px;
    font-weight: 600;
    cursor: pointer;
    transition: transform 0.3s ease;

    &:hover {
      transform: translateY(-2px);
    }
  }
`;

const Home = () => {
  const [products, setProducts] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  
  const heroRef = useRef(null);
  const bannerRef = useRef(null);
  
  const { scrollYProgress } = useScroll();
  
  const heroBackgroundY = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);
  const heroOverlayY = useTransform(scrollYProgress, [0, 1], ['0%', '20%']);
  const heroContentY = useTransform(scrollYProgress, [0, 1], ['0%', '10%']);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const bannerY = useTransform(scrollYProgress, [0.5, 1], ['-20%', '0%']);
  
  const smoothBackgroundY = useSpring(heroBackgroundY, { stiffness: 100, damping: 30 });
  const smoothBannerY = useSpring(bannerY, { stiffness: 100, damping: 30 });

  const [featuresRef, featuresInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [testimonialsRef, testimonialsInView] = useInView({ triggerOnce: true, threshold: 0.1 });

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const data = await getProducts({ featured: true });
        setFeaturedProducts(data.slice(0, 6));
        setProducts(data);
      } catch (error) {
        console.error('Error fetching products:', error);
      }
    };
    
    fetchProducts();
  }, []);

  const stats = [
    { number: '50+', label: 'Unique Flavors' },
    { number: '100k+', label: 'Happy Customers' },
    { number: '10+', label: 'Years of Excellence' },
  ];

  const features = [
    { icon: FiStar, title: 'Premium Quality', description: 'Made with the finest ingredients' },
    { icon: FiHeart, title: 'Made with Love', description: 'Each piece crafted with care' },
    { icon: FiAward, title: 'Award Winning', description: 'Recognized for excellence' },
    { icon: FiTruck, title: 'Worldwide Shipping', description: 'Delivered to your doorstep' },
  ];

  const testimonials = [
    {
      text: 'The best toffees I have ever tasted! The caramel crunch is absolutely divine.',
      author: 'Sarah Johnson',
      role: 'Food Blogger',
      image: '/assets/testimonials/sarah.jpg'
    },
    {
      text: 'Kemlo candies are my go-to gift for friends and family. Always a hit!',
      author: 'Michael Chen',
      role: 'Loyal Customer',
      image: '/assets/testimonials/michael.jpg'
    },
    {
      text: 'Incredible variety and consistent quality. The mint fresh is perfection.',
      author: 'Emily Rodriguez',
      role: 'Candy Enthusiast',
      image: '/assets/testimonials/emily.jpg'
    }
  ];

  const instagramPosts = [
  { img: "/assets/post1.jpg", link: "https://www.instagram.com/kemlocandy/p/DVfVFFjAW3s/" },
  { img: "/assets/post2.jpg", link: "https://www.instagram.com/kemlocandy/p/DVEiv60klhp/" },
  { img: "/assets/post3.jpg", link: "https://www.instagram.com/kemlocandy/p/DVP60kPiAoo/" },
  { img: "/assets/post4.jpg", link: "https://www.instagram.com/kemlocandy/p/DVIQLOniP29/" },
  { img: "/assets/post5.jpg", link: "https://www.instagram.com/kemlocandy/p/DVB3WkYCJPc/" },
  { img: "/assets/post6.jpg", link: "https://www.instagram.com/kemlocandy/p/DVLKwATAcvL/" },
  { img: "/assets/post7.jpg", link: "https://www.instagram.com/kemlocandy/p/DVLKwATAcvL/" },
  { img: "/assets/post8.jpg", link: "https://www.instagram.com/kemlocandy/p/DVAmK3ygTyI/" },
];

  return (
    <HomeContainer>
      <HeroSection ref={heroRef}>
        <BackgroundImage style={{ y: smoothBackgroundY }} />
        <GradientOverlay style={{ y: heroOverlayY }} />
        <PatternOverlay style={{ y: heroOverlayY }} />
        
        <HeroContent style={{ y: heroContentY, opacity: heroOpacity }}>
          <HeroBadge
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            CONFECTION MASTERS
          </HeroBadge>
          
          <HeroTitle
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Crafting Sweet
            <br />
            <span>Moments</span> Since 1993
          </HeroTitle>
          
          <HeroDescription
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            Discover our handcrafted toffees made with love and the finest ingredients. 
            Each piece tells a story of passion and dedication to sweetness.
          </HeroDescription>
          
          <HeroStats
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            {stats.map((stat, index) => (
              <StatItem key={index}>
                <StatNumber>{stat.number}</StatNumber>
                <StatLabel>{stat.label}</StatLabel>
              </StatItem>
            ))}
          </HeroStats>
          
          <CTAButton
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Explore Collection
          </CTAButton>
        </HeroContent>
        
        <ScrollIndicator
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          onClick={() => {
            window.scrollTo({
              top: window.innerHeight,
              behavior: 'smooth'
            });
          }}
        >
          <ScrollText>Scroll</ScrollText>
          <ScrollLine
            animate={{
              scaleY: [1, 1.5, 1],
              opacity: [0.5, 1, 0.5]
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
        </ScrollIndicator>
      </HeroSection>

<ProductShowcase products={featuredProducts} />

      <FeaturesSection ref={featuresRef}>
        {features.map((feature, index) => (
          <FeatureCard
            key={index}
            initial={{ opacity: 0, y: 30 }}
            animate={featuresInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: index * 0.1 }}
            whileHover={{ y: -5 }}
          >
            <feature.icon />
            <h3>{feature.title}</h3>
            <p>{feature.description}</p>
          </FeatureCard>
        ))}
      </FeaturesSection>

      <FeaturedSection>
        <SectionHeader>
          <SectionTitle>
            <span>FEATURED PRODUCTS</span>
            Our Most Loved <br />
            Candy Creations
          </SectionTitle>
          <ViewAllLink
            href="/catalog"
            whileHover={{ x: 5 }}
          >
            View All Products <FiChevronRight />
          </ViewAllLink>
        </SectionHeader>
        
        <ProductsGrid>
          {featuredProducts.map((product, index) => (
            <ProductCard key={product.id} product={product} index={index} />
          ))}
        </ProductsGrid>
      </FeaturedSection>

      <ParallaxBanner ref={bannerRef}>
        <BannerImage style={{ y: smoothBannerY }} />
        <BannerContent>
          <BannerTitle>Sweet Memories Await</BannerTitle>
          <BannerSubtitle>
            Join our newsletter and get 20% off your first order
          </BannerSubtitle>
          <BannerButton
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Subscribe Now
          </BannerButton>
        </BannerContent>
      </ParallaxBanner>

      <TestimonialsSection ref={testimonialsRef}>
        <SectionTitle style={{ textAlign: 'center' }}>
          <span>TESTIMONIALS</span>
          What Our Customers Say
        </SectionTitle>

        <TestimonialsGrid>
          {testimonials.map((testimonial, index) => (
            <TestimonialCard
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={testimonialsInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: index * 0.1 }}
              whileHover={{ y: -5 }}
            >
              <p>{testimonial.text}</p>
              <div className="author">
                <img src={testimonial.image} alt={testimonial.author} />
                <div>
                  <h4>{testimonial.author}</h4>
                  <span>{testimonial.role}</span>
                </div>
              </div>
            </TestimonialCard>
          ))}
        </TestimonialsGrid>
      </TestimonialsSection>

      <InstagramSection>
        <SectionTitle style={{ textAlign: 'center' }}>
          <span>FOLLOW US</span>
          @kemlocandy on Instagram
        </SectionTitle>

        <InstagramGrid>
  {instagramPosts.map((post, index) => (
    <InstagramPost key={index}>
      <a href={post.link} target="_blank" rel="noopener noreferrer">
        <img src={post.img} alt="Instagram post" />
      </a>
    </InstagramPost>
  ))}
</InstagramGrid>
      </InstagramSection>

      <NewsletterSection>
        <SectionTitle style={{ color: 'white' }}>
          <span style={{ color: 'var(--primary-color)' }}>NEWSLETTER</span>
          Stay Sweetly Updated
        </SectionTitle>
        <p style={{ color: 'rgba(255,255,255,0.7)', maxWidth: '600px', margin: '0 auto' }}>
          Subscribe to receive updates on new flavors, special offers, and sweet surprises!
        </p>

        <NewsletterForm onSubmit={(e) => e.preventDefault()}>
          <input 
            type="email" 
            placeholder="Your email address" 
            required 
          />
          <button type="submit">Subscribe</button>
        </NewsletterForm>
      </NewsletterSection>
    </HomeContainer>
  );
};

export default Home;